import { useState, useEffect, useRef, useCallback } from 'react';
import styles from './HowItWorks.module.css';
const DESKTOP_MQ = '(min-width: 769px)';

/* ── Persona data with slides ────────────────────────────── */
const PERSONA_DATA = [
  {
    id: 'teacher',
    gradientLabel: 'For',
    gradientTitle: 'Teacher',
    gradient: 'linear-gradient(107.63deg, #88DE88 5.3%, #C4EDC4 83.35%)',
    mockup: 'ipad',
    slides: [
      {
        feature: 'Teacher Assigns Homework',
        body: "Set goals and assign differentiated homework — aligned to students' needs.",
        screenSrc: '/videos/landing/teacher-1.mp4',
      },
      {
        feature: 'Get insights without correction load',
        body: 'See learning gaps by student and concept — without manual correction.',
        screenSrc: '/videos/landing/teacher-2.mp4',
      },
      {
        feature: 'Intervene where it matters',
        body: 'Give every student the right support — in one click.',
        screenSrc: '/videos/landing/teacher-3.mp4',
      },
      {
        feature: 'Get adaptive lesson plans',
        body: 'Get lesson plans that update daily based on class gaps.',
        screenSrc: '/videos/landing/teacher-4.mp4',
      },
    ],
  },
  {
    id: 'student',
    gradientLabel: 'For',
    gradientTitle: 'Student',
    gradient: 'linear-gradient(105.98deg, #ED927E 2.28%, #FFCFC5 80.36%)',
    mockup: 'ipad',
    slides: [
      {
        feature: 'Do homework without getting stuck',
        body: 'AI tutor, Vin supports students without giving away answers — building the habit of finding them.',
        screenSrc: '/videos/landing/student-1.mp4',
      },
      {
        feature: 'Get a personalised study plan',
        body: "Study plans adapt to each student's gaps and goals — and update as they improve.",
        screenSrc: '/videos/landing/student-2.mp4',
      },
      {
        feature: 'See their learning index',
        body: "Students see their learning journey over time — what's mastered and what needs focus.",
        screenSrc: '/videos/landing/student-3.mp4',
      },
    ],
  },
  {
    id: 'parent',
    gradientLabel: 'For',
    gradientTitle: 'Parent',
    gradient: 'linear-gradient(105.85deg, #BBB2F1 2.28%, #DDD7FF 80.36%)',
    mockup: 'iphone',
    slides: [
      {
        feature: 'Get insights on their child',
        body: 'Clear visibility into strengths, gaps, and progress — without needing to ask.',
        screenSrc: '/videos/landing/parent-1.mp4',
      },
      {
        feature: 'Support learning at home',
        body: 'Simple, actionable steps help parents support learning beyond school.',
        screenSrc: '/videos/landing/parent-2.mp4',
      },
    ],
  },
  {
    id: 'admin',
    gradientLabel: 'For',
    gradientTitle: 'Leadership',
    gradient: 'linear-gradient(107.24deg, #C4C4C4 2.28%, #E3E3E3 80.36%)',
    mockup: 'iphone',
    slides: [
      {
        feature: 'Get school-wide insights',
        body: 'A single view of learning across classes, grades, and subjects — at a glance.',
        screenSrc: '/videos/landing/leadership-1.mp4',
      },
      {
        feature: 'Get alerts, take action',
        body: 'Real-time alerts highlight issues early — with clear next steps to act fast.',
        screenSrc: '/videos/landing/leadership-2.mp4',
      },
    ],
  },
];


/* ── Video mockup — lazy load + play only when visible ───── */
function VideoMockup({ src, alt, visible, shouldLoad, paused, wrapClass, videoClass }) {
  const videoRef = useRef(null);
  // Track whether this video's src has ever been set (don't unload once loaded)
  const loadedRef = useRef(false);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    if (shouldLoad && !loadedRef.current) {
      loadedRef.current = true;
      el.src = src;
      el.load();
    }

    if (visible && !paused) {
      el.play().catch(() => { });
    } else {
      el.pause();
    }
  }, [visible, paused, shouldLoad, src]);

  return (
    <div className={`${wrapClass} ${visible ? styles.ipadVisible : styles.ipadHidden}`}>
      <video
        ref={videoRef}
        className={videoClass}
        muted
        playsInline
        loop
        preload="none"
        aria-label={alt}
      />
    </div>
  );
}

function IpadMockup({ src, alt, visible, shouldLoad, paused }) {
  return <VideoMockup src={src} alt={alt} visible={visible} shouldLoad={shouldLoad} paused={paused} wrapClass={styles.ipadWrap} videoClass={styles.ipadScreen} />;
}

function IphoneMockup({ src, alt, visible, shouldLoad, paused }) {
  return <VideoMockup src={src} alt={alt} visible={visible} shouldLoad={shouldLoad} paused={paused} wrapClass={styles.iphoneWrap} videoClass={styles.iphoneScreen} />;
}

/* ── Nav chevron (desktop carousel arrows) ───────────────── */
function Chevron({ dir }) {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true"
      style={dir === 'right' ? { transform: 'scaleX(-1)' } : undefined}>
      <path d="M18.0303 22.7803C17.7374 23.0732 17.2626 23.0732 16.9697 22.7803L8.21967 14.0303C8.07902 13.8897 8 13.6989 8 13.5C8 13.3011 8.07902 13.1103 8.21967 12.9697L16.9697 4.21967C17.2626 3.92678 17.7374 3.92678 18.0303 4.21967C18.3232 4.51256 18.3232 4.98744 18.0303 5.28033L9.81066 13.5L18.0303 21.7197C18.3232 22.0126 18.3232 22.4874 18.0303 22.7803Z" fill="#616161" />
    </svg>
  );
}

/* ── Persona section — text scroll + image crossfade ─────── */
function PersonaSection({ persona }) {
  const sectionRef = useRef(null);
  const scrollRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const slideCount = persona.slides.length;
  const [inViewport, setInViewport] = useState(false);
  const [paused, setPaused] = useState(false);
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.(DESKTOP_MQ).matches
  );
  // Only hover-capable devices should pause on hover (avoids stuck pause after a tap on touch)
  const canHoverRef = useRef(false);
  // While a programmatic smooth-scroll runs, ignore scroll-position sync so the
  // video crossfade doesn't flicker back to the previous slide mid-animation.
  const suppressScrollSyncRef = useRef(false);
  const suppressTimerRef = useRef(null);

  // Scroll text to a given index
  const scrollTo = useCallback((idx) => {
    const el = scrollRef.current;
    if (!el) return;
    const slideW = el.firstChild?.offsetWidth || el.offsetWidth;
    el.scrollTo({ left: idx * slideW, behavior: 'smooth' });
  }, []);

  // Authoritative navigation — set the index immediately (so the video crossfade
  // stays in sync with the text) and animate the text to match. Wraps around.
  const goTo = useCallback((idx) => {
    const next = ((idx % slideCount) + slideCount) % slideCount;
    setActiveIndex(next);
    suppressScrollSyncRef.current = true;
    clearTimeout(suppressTimerRef.current);
    suppressTimerRef.current = setTimeout(() => {
      suppressScrollSyncRef.current = false;
    }, 700);
    scrollTo(next);
  }, [slideCount, scrollTo]);

  // Sync activeIndex from manual scroll / swipe position
  const handleScroll = useCallback(() => {
    if (suppressScrollSyncRef.current) return;
    const el = scrollRef.current;
    if (!el) return;
    const slideW = el.firstChild?.offsetWidth || el.offsetWidth;
    const idx = Math.round(el.scrollLeft / slideW);
    setActiveIndex(Math.min(idx, slideCount - 1));
  }, [slideCount]);

  useEffect(() => () => clearTimeout(suppressTimerRef.current), []);

  useEffect(() => {
    canHoverRef.current = typeof window !== 'undefined'
      && window.matchMedia?.('(hover: hover)').matches;
  }, []);

  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_MQ);
    const onChange = () => setIsDesktop(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Track viewport visibility
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setInViewport(entry.isIntersecting);
      },
      { threshold: 0.2 }
    );
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  // Auto carousel — advances the shared index (text + video together).
  // Pauses while hovered so the user can rest on a specific card.
  useEffect(() => {
    if (!inViewport || paused) return;

    const timer = setInterval(() => {
      goTo(activeIndex + 1);
    }, 6000);

    return () => clearInterval(timer);
  }, [inViewport, paused, activeIndex, goTo]);

  const touchStartX = useRef(0);

  const handleVideoTouchStart = useCallback((e) => {
    touchStartX.current = e.changedTouches[0].screenX;
  }, []);

  const handleVideoTouchEnd = useCallback((e) => {
    const touchEndX = e.changedTouches[0].screenX;
    const diff = touchStartX.current - touchEndX;

    if (Math.abs(diff) > 40) { // minimum swipe distance
      goTo(diff > 0 ? activeIndex + 1 : activeIndex - 1);
    }
  }, [activeIndex, goTo]);

  const pause = useCallback(() => { if (canHoverRef.current) setPaused(true); }, []);
  const resume = useCallback(() => { if (canHoverRef.current) setPaused(false); }, []);

  return (
    <div
      ref={sectionRef}
      className={styles.persona}
      data-persona={persona.id}
      onMouseEnter={pause}
      onMouseLeave={resume}
    >
      {/* Gradient title — 2-line: small label + large name */}
      <div
        className={styles.gradientTitle}
        style={{ backgroundImage: persona.gradient }}
      >
        <span className={styles.gradientLabel}>{persona.gradientLabel}</span>
        <span className={styles.gradientName}>{persona.gradientTitle}</span>
      </div>

      {/* Functional pagination dots */}
      <div className={styles.dots}>
        {persona.slides.map((_, i) => (
          <span
            key={i}
            className={`${styles.dot} ${i === activeIndex ? styles.dotActive : ''}`}
            onClick={() => goTo(i)}
            style={{ cursor: 'pointer' }}
          />
        ))}
      </div>

      {/* Text — horizontal scroll, clipped */}
      <div className={styles.textClip}>
        <div
          ref={scrollRef}
          className={styles.textScroll}
          onScroll={handleScroll}
        >
          {persona.slides.map((slide, i) => (
            <div key={i} className={styles.textSlide}>
              <h3 className={styles.slideFeature}>{slide.feature}</h3>
              <p className={styles.slideBody}>{slide.body}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Image row — arrows flank the crossfading video (desktop; hidden on mobile) */}
      <div className={styles.mediaRow}>
        <button
          type="button"
          className={styles.navArrow}
          onClick={() => goTo(activeIndex - 1)}
          aria-label="Previous slide"
        >
          <Chevron dir="left" />
        </button>

        <div
          className={persona.mockup === 'iphone' ? styles.imageAreaPhone : styles.imageArea}
          onTouchStart={handleVideoTouchStart}
          onTouchEnd={handleVideoTouchEnd}
        >
          {persona.slides.map((slide, i) => {
            const isNeighbour = isDesktop && (i === (activeIndex + 1) % slideCount || i === (activeIndex - 1 + slideCount) % slideCount);
            const shouldLoad = inViewport && (i === activeIndex || isNeighbour);
            return persona.mockup === 'iphone' ? (
              <IphoneMockup
                key={i}
                src={slide.screenSrc}
                alt={`${slide.feature} screen`}
                visible={inViewport && i === activeIndex}
                shouldLoad={shouldLoad}
                paused={paused}
              />
            ) : (
              <IpadMockup
                key={i}
                src={slide.screenSrc}
                alt={`${slide.feature} screen`}
                visible={inViewport && i === activeIndex}
                shouldLoad={shouldLoad}
                paused={paused}
              />
            );
          })}
        </div>

        <button
          type="button"
          className={styles.navArrow}
          onClick={() => goTo(activeIndex + 1)}
          aria-label="Next slide"
        >
          <Chevron dir="right" />
        </button>
      </div>
    </div>
  );
}

/* ── Main component ──────────────────────────────────────── */
export default function HowItWorks({ handleBtnClick }) {
  return (
    <section id="how-it-works" className={styles.section} aria-label="How CoSchool works">

      {/* ── HOW IT WORKS label + connector with shimmer ──── */}
      <div className={styles.howLabelGroup}>
        <p className={styles.howLabel}>How it works</p>
        <div className={styles.howConnector} aria-hidden="true" />
      </div>

      {/* ── Persona sections ─────────────────────────────── */}
      <div className={styles.personasList}>
        {PERSONA_DATA.map((persona) => (
          <PersonaSection key={persona.id} persona={persona} />
        ))}
      </div>

      {/* ── Bottom CTA ───────────────────────────────────── */}
      <div className={styles.ctaWrap}>
        <button
          onClick={(e) => handleBtnClick(e.target, "Take Walkthrough CTA")}
          className={`wm-btn-primary ${styles.ctaBtn}`}
        >
          Try SchoolAi 
        </button>
      </div>
    </section>
  );
}
